"use client"

import { useState, useEffect } from "react"
import { getOrganizationStructure, getCompleteDepartment } from "api/apiHelper"
import type EmployeeType from "types/employee-types"

interface Level {
  id: number
  code: string
  order: number
  promotion_timeline: string
}

interface Title {
  id: number
  name: string
  level: Level
  department: {
    id: number
    name: string
  }
  specialization?: string
  tags?: string[]
}

// Enhanced employee type for the levels system
interface EnhancedEmployeeType extends EmployeeType {
  title_details?: Title
}

interface LevelWithTitle extends Level {
  title?: Title
  allTitles?: Title[]
  isCurrentLevel: boolean
  canPromoteTo?: boolean
}

// Main component export
const LevelSystemCard = ({ employeeData }: { employeeData?: EmployeeType }) => {
  const [showDetails, setShowDetails] = useState<number | null>(null)
  const [employee, setEmployee] = useState<EnhancedEmployeeType | null>(null)
  const [availableLevels, setAvailableLevels] = useState<LevelWithTitle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // API calls using your actual API helpers
  const fetchEmployeeWithTitleDetails = async (empData: EmployeeType): Promise<EnhancedEmployeeType | null> => {
    try {
      console.log("Fetching employee path for ID:", empData.id)

      // This hits your employee_path endpoint
      const response = await getOrganizationStructure(empData.id.toString())
      const pathData = response.data

      console.log("Employee path API Response:", pathData)

      if (pathData && pathData.length > 0) {
        // Find the current employee in the path (the one with matching ID)
        const currentEmployeeData = pathData.find(
          (emp: any) => emp.id === empData.id || emp.id.toString() === empData.id.toString(),
        )

        console.log("Found current employee in path:", currentEmployeeData)

        if (currentEmployeeData && currentEmployeeData.title) {
          console.log("Employee has title details:", currentEmployeeData.title)
          return {
            ...empData,
            title_details: {
              id: currentEmployeeData.title.id,
              name: currentEmployeeData.title.name,
              level: {
                id: currentEmployeeData.title.level.id,
                code: currentEmployeeData.title.level.code,
                order: currentEmployeeData.title.level.order,
                promotion_timeline: currentEmployeeData.title.level.promotion_timeline || "",
              },
              department: {
                id: currentEmployeeData.title.department.id,
                name: currentEmployeeData.title.department.name,
              },
              specialization: currentEmployeeData.title.specialization,
              tags: currentEmployeeData.title.tags,
            },
          }
        }
      }

      console.log("No title details found in path")
      return { ...empData }
    } catch (error) {
      console.error("Error fetching employee path:", error)
      return { ...empData }
    }
  }

  const fetchDepartmentStructure = async (
    departmentId: number,
  ): Promise<{ levels: LevelWithTitle[]; titles: any[] }> => {
    try {
      console.log("Fetching department structure for ID:", departmentId)

      // This hits your with_levels_and_titles endpoint
      const response = await getCompleteDepartment(departmentId.toString())
      const data = response.data

      console.log("Department structure API Response:", data)

      const titlesByLevel = data.titles.reduce((acc: any, title: any) => {
        if (!acc[title.level_id]) {
          acc[title.level_id] = []
        }
        acc[title.level_id].push(title)
        return acc
      }, {})

      // Transform the data to match our component structure
      const levelsWithTitles = data.levels.map((level: any) => {
        const levelTitles = titlesByLevel[level.id] || []
        const primaryTitle =
          levelTitles.find((title: any) => employee?.title_details?.id === title.id) || levelTitles[0]

        return {
          id: Number.parseInt(level.id),
          code: level.code,
          order: Number.parseInt(level.order),
          promotion_timeline: level.promotion_timeline || "",
          title: primaryTitle,
          allTitles: levelTitles,
          isCurrentLevel: false,
          canPromoteTo: false,
        }
      })

      console.log("Processed levels with titles:", levelsWithTitles)

      return {
        levels: levelsWithTitles,
        titles: data.titles,
      }
    } catch (error) {
      console.error("Error fetching department structure:", error)
      throw error
    }
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("=== Starting Level System Data Load ===")
        setLoading(true)
        setError(null)

        if (!employeeData) {
          console.error("No employee data provided")
          setError("Employee data is required")
          return
        }

        console.log("Employee data received:", employeeData)

        // Get enhanced employee data with title details
        const enhancedEmployee = await fetchEmployeeWithTitleDetails(employeeData)
        if (!enhancedEmployee) {
          console.error("Failed to load enhanced employee data")
          setError("Failed to load employee data")
          return
        }

        console.log("Enhanced employee data:", enhancedEmployee)
        setEmployee(enhancedEmployee)

        // If we don't have title details, we can't show the career progression
        if (!enhancedEmployee.title_details) {
          console.warn("Employee title information not available")
          setError(
            "Employee title information not available. This employee may not be assigned to a career level system.",
          )
          return
        }

        console.log("Employee has title details, fetching department structure...")
        const departmentStructure = await fetchDepartmentStructure(enhancedEmployee.title_details.department.id)

        // Show ALL levels in the department, but highlight current and mark future levels
        const currentLevelOrder = enhancedEmployee.title_details.level.order
        console.log("Current level order:", currentLevelOrder)

        const allLevels = departmentStructure.levels
          .sort((a, b) => a.order - b.order)
          .map((level) => ({
            ...level,
            isCurrentLevel: level.id === enhancedEmployee.title_details!.level.id,
            canPromoteTo: level.order > currentLevelOrder && level.order === currentLevelOrder + 1,
            monthsInCurrentLevel: level.id === enhancedEmployee.title_details!.level.id,
          }))

        console.log("All levels processed:", allLevels)
        setAvailableLevels(allLevels)
        console.log("=== Level System Data Load Completed Successfully ===")
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred while loading career progression data"
        console.error("Error in loadData:", errorMessage, err)
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [employeeData])

  const getStatusColor = (level: LevelWithTitle) => {
    if (level.isCurrentLevel) return "bg-blue-500"
    if (level.canPromoteTo) return "bg-green-500"
    if (level.order > (employee?.title_details?.level.order || 0)) return "bg-gray-400"
    return "bg-gray-300" // Past levels
  }

  const getStatusLabel = (level: LevelWithTitle) => {
    if (level.isCurrentLevel) return { color: "bg-blue-200", text: "Current" }
    if (level.canPromoteTo) return { color: "bg-green-200", text: "Next" }
    if (level.order > (employee?.title_details?.level.order || 0)) return { color: "bg-gray-300", text: "Future" }
    return { color: "bg-gray-100", text: "Previous" }
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md p-6">
        <div className="text-center">
          <div className="text-yellow-500 mb-2">
            <i className="fas fa-info-circle text-2xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Career Level Information</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>

          {/* Show basic employee info even when career levels aren't available */}
          {employeeData && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-navy-700 rounded-lg text-left">
              <h4 className="font-medium text-gray-800 dark:text-white mb-3">Employee Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="mb-2">
                    <span className="font-medium">Name:</span> {employeeData.first_name} {employeeData.last_name}
                  </p>
                  <p className="mb-2">
                    <span className="font-medium">Designation:</span> {employeeData.designation}
                  </p>
                  <p className="mb-2">
                    <span className="font-medium">Company:</span> {employeeData.company}
                  </p>
                </div>
                <div>
                  {employeeData.date_joined && (
                    <p className="mb-2">
                      <span className="font-medium">Date Joined:</span>{" "}
                      {new Date(employeeData.date_joined).toLocaleDateString()}
                    </p>
                  )}
                  {employeeData.manager_name && (
                    <p className="mb-2">
                      <span className="font-medium">Manager:</span> {employeeData.manager_name}
                    </p>
                  )}
                  <p className="mb-2">
                    <span className="font-medium">Status:</span> {employeeData.is_active ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!employee) {
    return null
  }

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">Career Progression</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-300">Current Level:</span>
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
              {employee.title_details?.level.code || "N/A"}
            </span>
          </div>
        </div>

        {/* Employee Info */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-navy-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {employee.photo && (
                <img
                  src={employee.photo || "/placeholder.svg"}
                  alt={`${employee.first_name} ${employee.last_name}`}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none"
                  }}
                />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {employee.first_name} {employee.last_name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {employee.title_details?.department.name || employee.company}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">Title:</span>
                <span className="text-sm font-medium text-gray-800 dark:text-white">
                  {employee.title_details?.name}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="space-y-8">
            {availableLevels.map((level, index) => {
              const isExpanded = showDetails === level.id
              const isLast = index === availableLevels.length - 1

              return (
                <div key={level.id} className="relative">
                  {/* Connecting line to next level */}
                  {!isLast && (
                    <div className="absolute left-1/2 top-20 w-0.5 h-8 bg-gray-300 dark:bg-gray-600 transform -translate-x-0.5 z-0"></div>
                  )}

                  {/* Main level container */}
                  <div className="flex items-start">
                    {/* Left content */}
                    <div className="flex-1 text-right pr-6">
                    <div className="flex items-center h-20 ml-10">
                      <div className="inline-block">
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusLabel(level).color} mb-2`}
                        >
                          {getStatusLabel(level).text}
                        </div>
                      </div>
                      </div>
                    </div>

                    {/* Center circle */}
                    <div className="relative z-10 flex-shrink-0">
                      <div
                        className={`flex items-center justify-center w-20 h-20 rounded-full ${getStatusColor(level)} text-white shadow-lg cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 border-4 border-white dark:border-navy-800`}
                        onClick={() => setShowDetails(isExpanded ? null : level.id)}
                      >
                        <span className="font-bold text-lg">{level.code}</span>
                      </div>

                      {/* Expand indicator */}
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                        <div
                          className={`w-6 h-6 rounded-full bg-white dark:bg-navy-800 shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors ${isExpanded ? "rotate-180" : ""} transition-transform duration-300`}
                          onClick={() => setShowDetails(isExpanded ? null : level.id)}
                        >
                          <i className="fas fa-chevron-down text-gray-400 text-xs"></i>
                        </div>
                      </div>
                    </div>

                    {/* Right content */}
                    <div className="flex-1 pl-6">
                        <div className="flex items-center h-20">
                            <div className="inline-block">
                            {level.promotion_timeline && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                Timeline: {level.promotion_timeline}
                                </p>
                            )}
                            </div>
                        </div>
                    </div>
                  </div>

                  {/* Expanded content with smooth animation */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        isExpanded ? "max-h-96 opacity-100 mt-6" : "max-h-0 opacity-0"
                    }`}
                    >
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-navy-700 dark:to-navy-600 rounded-xl p-6 mx-8 shadow-inner mt-2">
                        {/* Remove the grid layout and use full width */}
                        <div className="w-full">
                        {level.allTitles && level.allTitles.length > 0 && (
                            <div className="w-full">
                            <h4 className="font-medium text-gray-800 dark:text-white mb-3 flex items-center">
                                <i className="fas fa-briefcase text-green-500 mr-2"></i>
                                Available Titles
                            </h4>
                            <div className="space-y-2 max-h-32 overflow-y-auto w-full">
                                {level.allTitles.map((title: any) => {
                                const isCurrentEmployeeTitle = String(employee?.title_details?.id) === title.id
                                return (
                                    <div
                                    key={title.id}
                                    className="w-full flex items-center justify-between p-1 rounded-lg transition-all duration-200"
                                    >
                                    <div className={`w-full flex items-center justify-between ${
                                        isCurrentEmployeeTitle
                                        ? "bg-blue-100 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600 shadow-md"
                                        : "bg-white dark:bg-navy-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-navy-700 hover:shadow-md"
                                    } p-3 rounded-lg`}>
                                        <span className="text-sm font-medium text-gray-800 dark:text-white">
                                        {title.name}
                                        </span>
                                        {isCurrentEmployeeTitle && (
                                        <span className="px-2 py-1 bg-blue-500 text-white rounded-full text-xs font-bold animate-pulse">
                                            YOU
                                        </span>
                                        )}
                                    </div>
                                    </div>
                                )
                                })}
                            </div>
                            </div>
                        )}
                        </div>
                    </div>
                    </div>
                </div>
              )
            })}
          </div>
        </div>


        <div className="mt-8 p-4 bg-gray-50 dark:bg-navy-700 rounded-lg">
          <h4 className="font-medium text-gray-800 dark:text-white mb-3">Legend</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Current Level</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Next Level</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Future Level</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-gray-300 rounded-full mr-2"></div>
              <span className="text-gray-600 dark:text-gray-300">Previous Level</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LevelSystemCard
